import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Send, CheckCircle2, X, CalendarClock } from "lucide-react";
import { fmtWhen } from "@/components/SchedulePicker";
import { MailJobProgress, notifyMailJobsDone } from "@/components/MailJobProgress";

export function MailJobsIndicator() {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(null);
  const prevRunning = useRef(new Set());
  const load = useCallback(() => api.get("/mail-jobs").then((r) => {
    const now = new Set(r.data.filter((j) => j.status === "running").map((j) => j.id));
    if ([...prevRunning.current].some((id) => !now.has(id))) notifyMailJobsDone();
    prevRunning.current = now;
    setJobs(r.data);
  }).catch(() => setJobs([])), []);
  useEffect(() => {
    load();
    window.addEventListener("mailjobs", load);
    return () => window.removeEventListener("mailjobs", load);
  }, [load]);
  const running = jobs.some((j) => j.status === "running" || (j.status === "scheduled" && new Date(j.scheduled_at) <= new Date()));
  useEffect(() => {
    if (!jobs.some((j) => j.status === "scheduled")) { return undefined; }
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [jobs, load]);
  useEffect(() => {
    if (!running) { return undefined; }
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);
  const seen = (id) => api.post(`/mail-jobs/${id}/seen`).then(load);
  if (!jobs.length && !open) return null;
  return (
    <>
      <div className="fixed top-4 right-4 z-[90] space-y-2 w-[min(20rem,calc(100vw-2rem))]" data-testid="mail-jobs-indicator">
        {jobs.map((j) => {
          const done = j.pos ?? (j.sent.length + j.skipped.length + j.failed.length);
          const run = j.status === "running";
          const plan = j.status === "scheduled";
          return (
            <div key={j.id} className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-sm" data-testid={`mail-job-${j.id}`}>
              <div className="flex items-start gap-2">
                {plan ? <CalendarClock size={16} className="text-blue-600 mt-0.5" /> : run ? <Send size={16} className="text-blue-600 mt-0.5 animate-pulse" /> : <CheckCircle2 size={16} className={j.failed.length ? "text-amber-500 mt-0.5" : "text-emerald-600 mt-0.5"} />}
                <button className="flex-1 text-left" onClick={() => setOpen(j.id)} data-testid={`mail-job-open-${j.id}`}>
                  <div className="font-semibold text-slate-800 truncate">{plan ? "Serienmail geplant" : run ? "Serienmail läuft" : "Serienmail abgeschlossen"}</div>
                  <div className="text-xs text-slate-500 truncate">{j.label}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{plan ? `${fmtWhen(j.scheduled_at)} · ${j.total} Empfänger` : run ? `${done} von ${j.total} verarbeitet` : `${j.sent.length} gesendet · ${j.skipped.length} übersprungen · ${j.failed.length} fehlgeschlagen`}</div>
                </button>
                {!run && !plan && <button onClick={() => seen(j.id)} className="text-slate-400 hover:text-slate-700" title="Ausblenden" data-testid={`mail-job-dismiss-${j.id}`}><X size={14} /></button>}
              </div>
              {run && <div className="h-1.5 mt-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-600 transition-[width] duration-500" style={{ width: `${j.total ? (done / j.total) * 100 : 0}%` }} /></div>}
            </div>
          );
        })}
      </div>
      <Dialog open={!!open} onOpenChange={(o) => { if (!o) { const j = jobs.find((x) => x.id === open); if (j && j.status !== "running" && j.status !== "scheduled") seen(j.id); setOpen(null); load(); } }}>
        <DialogContent className="max-w-xl" data-testid="mail-job-dialog">
          <DialogHeader><DialogTitle>Serienmail</DialogTitle><DialogDescription>{jobs.find((x) => x.id === open)?.label}</DialogDescription></DialogHeader>
          {open && <MailJobProgress jobId={open} onJob={setOpen} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
